<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConsultationsStore } from '@/stores';
import ErrorBanner from '@/components/ErrorBanner.vue';
import type { Locales } from '@/services/types/api.types';

// PROPS
const props = defineProps<{
  consultationId: number;
  languageEnabled: Locales[];
}>();

// CONST
const consultationsStore = useConsultationsStore();
const reportLanguage = ref(props.languageEnabled[0]);
const errorReport = ref(false);
const reportGenerationInProgress = ref(false);
const reportCsvGenerationInProgress = ref(false);
const { t } = useI18n();

// FUNCTIONS
const exportExcel = async () => {
  errorReport.value = false;
  reportGenerationInProgress.value = true;
  try {
    const blob = await consultationsStore.getExcelReport(props.consultationId, reportLanguage.value);
    if (!blob) throw new Error('Failed to fetch Excel report');
    await downloadReport(blob, 'xlsx');
  } catch (error) {
    errorReport.value = true;
  } finally {
    reportGenerationInProgress.value = false;
  }
};

const exportRawCsv = async () => {
  errorReport.value = false;
  reportCsvGenerationInProgress.value = true;
  try {
    // Fetch the zip file from the API
    const blob = await consultationsStore.getRawCsvReport(props.consultationId, reportLanguage.value);
    if (!blob) throw new Error('Failed to fetch the zip file');
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'report.zip');
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    errorReport.value = true;
  } finally {
    reportCsvGenerationInProgress.value = false;
  }
};

const downloadReport = async (blob: Blob, fileExt: string) => {
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = `report.${fileExt}`;
  document.body.appendChild(link);
  link.dispatchEvent(
    new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    }),
  );
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
};
</script>

<template>
  <div class="consultation-results">
    <div class="consultation-results__export">
      <div class="consultation-results__export-title">{{ t('consultation.export.title') }}</div>
      <q-select
        v-model="reportLanguage"
        :options="props.languageEnabled"
        :label="t('consultation.export.language')"
        outlined
      />
      <div class="consultation-results__export-actions">
        <q-btn
          color="primary"
          :label="t('consultation.export.excel')"
          @click="exportExcel"
          :loading="reportGenerationInProgress"
        />
        <q-btn
          color="primary"
          :label="t('consultation.export.rawcsv')"
          @click="exportRawCsv"
          :loading="reportCsvGenerationInProgress"
        />
      </div>
      <p v-if="reportGenerationInProgress">
        {{ t('consultation.export.excelInProgress') }}
      </p>
      <error-banner :isError="errorReport" :error="t('consultation.export.errorDownload')" />
    </div>
  </div>
</template>

<style lang="scss">
.consultation-results {
  display: flex;
  flex-direction: column;

  &__export {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin: 0 0 20px 0;

    &-title {
      font-size: 20px;
      margin: 5px 0;
    }

    &-actions {
      display: flex;
      flex-direction: row;
      gap: 10px;

      .q-btn {
        width: 100%;
        padding: 12px;
      }
    }
  }

  .btn {
    width: 25%;
  }
}
</style>
